import React from "react";
import { ArrowRight } from "lucide-react";
// ─── Shared building blocks for the Data Hub overview page ─────────────────
// Kept in one file since they're small and only ever used by this page's
// own section components (welcome-banner.tsx, data-ecosystem.tsx, etc).

// Marks a card's number as sample/demo data (warehouse-metrics-api.ts's
// `is_sample: true`) rather than a live production total — per
// data-warehouse-dashboard-ui-integration.md: "Clearly badge or annotate
// rows where is_sample is true" / "don't treat sample metrics as live
// production totals."
export const SampleBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 rounded px-1.5 py-0.5">
    Sample
  </span>
);

// A colored square icon badge — e.g. the little icon next to "Unified
// Profiles" or inside a channel pill.
export const IconBadge: React.FC<{
  icon: React.ReactNode;
  className?: string;
}> = ({ icon, className = "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300" }) => (
  <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${className}`}>
    {icon}
  </span>
);

// One of the four "Data Hub Performance" / "Uploaded Data Overview" tiles —
// icon, label, big number, description, and a link at the bottom.
export const StatCard: React.FC<{
  icon: React.ReactNode;
  iconClassName?: string;
  label: string;
  value: string | number;
  valueClassName?: string;
  description: string;
  linkLabel?: string;
  onLinkClick?: () => void;
  loading?: boolean;
  isSample?: boolean;
}> = ({ icon, iconClassName, label, value, valueClassName, description, linkLabel, onLinkClick, loading, isSample }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 flex flex-col gap-2 shadow-sm">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
      <IconBadge icon={icon} className={iconClassName} />
      {label}
    </div>
    {loading ? (
      <div className="h-7 w-16 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
    ) : (
      <div className={`flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-gray-100 ${valueClassName || ""}`}>
        {value}
        {isSample && <SampleBadge />}
      </div>
    )}
    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
    {linkLabel && (
      <button
        type="button"
        onClick={onLinkClick}
        className="mt-auto pt-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline text-left w-fit"
      >
        {linkLabel}
      </button>
    )}
  </div>
);

// A small "channel" tile — used in "Connected Sources" / "Activated
// Destinations" (CSV, SQL, WhatsApp, Meta Ads, ...).
export const ChannelTile: React.FC<{
  icon: React.ReactNode;
  iconClassName?: string;
  label: string;
  count: number | string;
  loading?: boolean;
  isSample?: boolean;
}> = ({ icon, iconClassName, label, count, loading, isSample }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 flex flex-col gap-2.5 shadow-sm min-w-0">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 min-w-0">
      <IconBadge icon={icon} className={iconClassName} />
      <span className="truncate">{label}</span>
    </div>
    {loading ? (
      <div className="h-6 w-10 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
    ) : (
      <div className="flex items-center gap-1.5 text-xl font-semibold text-gray-900 dark:text-gray-100">
        {count}
        {isSample && <SampleBadge />}
      </div>
    )}
  </div>
);

// The wide "promo" row used for "Unify Connected Profiles" and "Explore
// unified customer profiles in one place" — title/description/count on the
// left, an illustration on the right, a primary button below the count.
export const PromoRow: React.FC<{
  title: string;
  description: string;
  count: number | string;
  countLabel: string;
  buttonLabel: string;
  illustration: React.ReactNode;
  onButtonClick?: () => void;
  loading?: boolean;
  isSample?: boolean;
}> = ({ title, description, count, countLabel, buttonLabel, illustration, onButtonClick, loading, isSample }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 sm:p-6 shadow-sm">
    <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
      <div className="flex-1 min-w-0">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xl">{description}</p>
        <div className="mt-4">
          {loading ? (
            <div className="h-7 w-16 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ) : (
            <div className="flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {count}
              {isSample && <SampleBadge />}
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">{countLabel}</p>
        </div>
        <button
          type="button"
          onClick={onButtonClick}
          className="mt-4 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm"
        >
          {buttonLabel}
        </button>
      </div>
      <div className="w-full md:w-auto shrink-0 flex justify-center">{illustration}</div>
    </div>
  </div>
);

// A simple round-cornered card icon (used for the Connect Sources / Activate
// Destinations columns' own channel-icon rows).
export const RoundIcon: React.FC<{ icon: React.ReactNode; className?: string }> = ({ icon, className = "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300" }) => (
  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md shrink-0 ${className}`}>
    {icon}
  </span>
);

export const SectionArrowLink: React.FC<{ label: string }> = ({ label }) => (
  <button
    type="button"
    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
  >
    {label} <ArrowRight size={12} />
  </button>
);
